import { useCallback, useEffect, useMemo, useState } from "react";
import { PenLineIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArbolDeSecciones } from "@/components/generacion/ArbolDeSecciones";
import { BotonIrACorregir } from "@/components/generacion/CorreccionPendiente";
import { PanelDeSeccion } from "@/components/generacion/PanelDeSeccion";
import { ProgresoDeLaRedaccion } from "@/components/generacion/ProgresoDeLaRedaccion";
import { useRedactarPendientes, useRedaccionSincronizada } from "@/hooks/useRedaccion";
import { getApiErrorMessage } from "@/lib/api";
import {
  filtrarSecciones,
  necesitaRevision,
  numerarSecciones,
  correccionesDeLaPuerta,
  redaccionCerrada,
  siguientePorRevisar,
  type CorridaDeLaRedaccion,
  type PuertaDeLaRedaccion,
  type VistaDeSecciones,
} from "@/lib/redaccion";

interface RedaccionDelInformeProps {
  informeId: number;
  /** La sección con que se abre la fase (la que pidió el checklist de emisión). */
  seccionInicialId?: number | null;
  /** Lleva a la fase 1, donde se responden las dudas. */
  onIrALasDudas?: () => void;
  /** Lleva a la fase 2, donde se corrigen los precios y las líneas. */
  onIrALaValorizacion?: () => void;
}

const plural = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`;

/**
 * La fase 3: las secciones del informe en su árbol, y a la derecha lo que
 * corresponde a la elegida — revisarla, regenerarla, editarla o validarla.
 *
 * La IA redacta y cita la hoja de hechos; nada se da por bueno hasta que una
 * persona lo valida. Las cifras las verifica el servidor: esta pantalla solo
 * muestra lo que encontró.
 */
export function RedaccionDelInforme({
  informeId,
  seccionInicialId = null,
  onIrALasDudas,
  onIrALaValorizacion,
}: RedaccionDelInformeProps) {
  const { secciones, corrida } = useRedaccionSincronizada(informeId, true, true);
  const redactar = useRedactarPendientes(informeId);

  const [elegida, setElegida] = useState<number | null>(seccionInicialId);
  const [vista, setVista] = useState<VistaDeSecciones>({ filtro: null, verTodo: false });
  // Hay un borrador a mano sin guardar y, si se pidió irse, a dónde.
  const [edicionSucia, setEdicionSucia] = useState(false);
  const [destinoPendiente, setDestinoPendiente] = useState<number | null>(null);

  // El navegador también avisa al cerrar o recargar con un borrador sin guardar.
  useEffect(() => {
    if (!edicionSucia) return;

    const avisar = (evento: BeforeUnloadEvent) => evento.preventDefault();
    window.addEventListener("beforeunload", avisar);

    return () => window.removeEventListener("beforeunload", avisar);
  }, [edicionSucia]);

  /** Cambiar de sección con un borrador sin guardar pregunta antes de perderlo. */
  const irA = useCallback(
    (itemId: number) => {
      if (itemId === elegida) return;

      if (edicionSucia) setDestinoPendiente(itemId);
      else setElegida(itemId);
    },
    [elegida, edicionSucia],
  );

  const datos = secciones.data;

  // La primera vez que llegan las secciones se abre la primera por revisar (o,
  // si no hay, la primera). Después manda la persona: re-calcularlo en cada
  // lectura saltaría de sección sola cada vez que el servidor se actualiza.
  useEffect(() => {
    if (elegida !== null || datos === undefined) return;

    const inicial = datos.secciones.find(necesitaRevision) ?? filtrarSecciones(datos.secciones, vista)[0];
    if (inicial) setElegida(inicial.item_id);
  }, [datos, elegida, vista]);

  // La numeración del documento sale de la lista COMPLETA: filtrar no cambia el «2.1» de nadie.
  const numeros = useMemo(() => numerarSecciones(datos?.secciones ?? []), [datos?.secciones]);

  if (secciones.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }

  if (secciones.isError || datos === undefined) {
    return (
      <Alert variant="destructive">
        <AlertTitle>No pudimos cargar las secciones del informe</AlertTitle>
        <AlertDescription>
          <p>{getApiErrorMessage(secciones.error, "Inténtalo de nuevo en unos segundos.")}</p>
          <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => void secciones.refetch()}>
            Reintentar
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (datos.secciones.length === 0) {
    return (
      <p className="text-meta text-ink-medio bg-card rounded-[1.25rem] border-[1.5px] px-4 py-8 text-center">
        Este informe todavía no tiene secciones.
      </p>
    );
  }

  const enCurso = corrida.data?.estado === "en_curso";
  const pendientes = datos.resumen.pendientes;
  const visibles = filtrarSecciones(datos.secciones, vista);
  const seleccionada = datos.secciones.find((s) => s.item_id === elegida) ?? null;

  const cerrada = redaccionCerrada(datos);

  return (
    <div className="space-y-4">
      {cerrada && datos.puerta ? (
        <AvisoDeRedaccionCerrada
          informeId={informeId}
          puerta={datos.puerta}
          onIr={onIrALasDudas}
          onIrALaValorizacion={onIrALaValorizacion}
        />
      ) : null}

      <div className="grid items-start gap-3.5 xl:grid-cols-[20.625rem_minmax(0,1fr)]">
        <ArbolDeSecciones
          secciones={visibles}
          todas={datos.secciones}
          numeros={numeros}
          seleccionadaId={elegida}
          vista={vista}
          onElegir={irA}
          onVista={setVista}
          cabecera={
            <RedaccionEnBloque
              pendientes={pendientes}
              enCurso={enCurso}
              cerrada={cerrada}
              redactar={redactar}
              corrida={corrida.data ?? null}
            />
          }
        />

        {seleccionada ? (
          <PanelDeSeccion
            key={seleccionada.item_id}
            informeId={informeId}
            seccion={seleccionada}
            numero={numeros.get(seleccionada.item_id) ?? null}
            datos={datos}
            siguiente={siguientePorRevisar(datos.secciones, seleccionada.item_id)}
            onIr={irA}
            onEdicionSucia={setEdicionSucia}
          />
        ) : (
          <p className="text-meta text-ink-medio bg-card rounded-[1.25rem] border-[1.5px] px-4 py-8 text-center">
            Elige una sección del árbol para verla.
          </p>
        )}
      </div>

      <AlertDialog open={destinoPendiente !== null} onOpenChange={(abierto) => !abierto && setDestinoPendiente(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tienes cambios sin guardar</AlertDialogTitle>
            <AlertDialogDescription>
              Editaste el texto de esta sección a mano y todavía no lo guardaste. Si cambias de sección, esos cambios se
              pierden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Seguir editando</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (destinoPendiente !== null) setElegida(destinoPendiente);
                setDestinoPendiente(null);
              }}
            >
              Descartar y cambiar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * La IA no redacta mientras haya dudas sin responder o falte el análisis. Lo dice
 * el servidor (`puerta`); acá solo se muestra y se ofrece el camino a las dudas.
 * Editar, validar y reabrir a mano siguen disponibles.
 */
function AvisoDeRedaccionCerrada({
  informeId,
  puerta,
  onIr,
  onIrALaValorizacion,
}: {
  informeId: number;
  puerta: PuertaDeLaRedaccion;
  onIr?: () => void;
  onIrALaValorizacion?: () => void;
}) {
  const correcciones = correccionesDeLaPuerta(puerta);

  return (
    <section role="alert" className="bg-warning-soft flex flex-wrap items-center gap-2.5 rounded-xl py-[0.5625rem] pr-3 pl-3.5">
      <span aria-hidden className="bg-warning-dot size-2 shrink-0 rounded-full" />
      <div className="text-warning-texto min-w-0 flex-1 text-[0.78125rem] font-bold text-pretty">
        {puerta.motivos.map((m) => (
          <p key={m.codigo}>{m.mensaje}</p>
        ))}
        {correcciones.length > 0 ? (
          <ul className="my-1.5 flex flex-col gap-1.5">
            {correcciones.map((c) => (
              <li key={c.duda_id} className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1">
                  Corrección pendiente: {c.campo} debe ser {c.valor_esperado ?? "—"} (hoy {c.valor_actual ?? "sin dato"})
                </span>
                <BotonIrACorregir informeId={informeId} destino={c.destino} onIrALaValorizacion={onIrALaValorizacion} />
              </li>
            ))}
          </ul>
        ) : null}
        <p className="font-semibold">
          Cuando esto se resuelva, la redacción empieza sola. Mientras tanto puedes editar y validar las secciones a
          mano.
        </p>
      </div>
      {onIr ? (
        <button
          type="button"
          onClick={onIr}
          className="bg-card text-warning-texto rounded-[0.5625rem] px-[0.6875rem] py-1.5 text-xs font-bold"
        >
          Ir a las dudas
        </button>
      ) : null}
    </section>
  );
}

/**
 * El avance de la redacción y, solo como salida, redactar las pendientes.
 *
 * LA REDACCIÓN ARRANCA SOLA (Rodgers, 26-10-2026): el servidor la pone en marcha
 * apenas se abre la puerta, así que el camino normal no pasa por ningún botón.
 * «Redactar las pendientes» queda como acción SECUNDARIA para cuando eso no
 * ocurrió —el arranque falló, o el informe es anterior a la regla— y nunca se
 * ofrece con la puerta cerrada ni con una redacción en curso. Va en la cabecera
 * de la tarjeta del árbol para no alterar la composición del diseño.
 */
function RedaccionEnBloque({
  pendientes,
  enCurso,
  cerrada,
  redactar,
  corrida,
}: {
  pendientes: number;
  enCurso: boolean;
  cerrada: boolean;
  redactar: ReturnType<typeof useRedactarPendientes>;
  corrida: CorridaDeLaRedaccion | null;
}) {
  const ofrecerRedactar = pendientes > 0 && !enCurso && !cerrada;

  if (!ofrecerRedactar && !redactar.isError && !corrida) return null;

  return (
    <div className="flex flex-col gap-2.5">
      {ofrecerRedactar ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-ink-muted text-xs font-semibold text-pretty">
            {plural(pendientes, "sección sigue pendiente", "secciones siguen pendientes")}. La redacción empieza sola,
            pero si no arrancó puedes iniciarla aquí.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-auto w-full rounded-[0.625rem] py-2 text-xs"
            disabled={redactar.isPending}
            onClick={() => redactar.mutate()}
          >
            <PenLineIcon aria-hidden />
            Redactar las pendientes ({pendientes})
          </Button>
        </div>
      ) : null}

      {redactar.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            <p>{getApiErrorMessage(redactar.error, "No pudimos iniciar la redacción. Inténtalo de nuevo.")}</p>
          </AlertDescription>
        </Alert>
      ) : null}

      {enCurso && corrida ? <ProgresoDeLaRedaccion corrida={corrida} /> : null}
      {!enCurso && corrida ? <ResultadoDeLaCorrida corrida={corrida} /> : null}
    </div>
  );
}

/** Cómo terminó la última corrida, solo si algo no salió: lo que salió bien ya se ve en el árbol. */
function ResultadoDeLaCorrida({ corrida }: { corrida: CorridaDeLaRedaccion }) {
  const { con_error: conError, omitidas, redactadas } = corrida.contadores;

  if (corrida.estado === "error" && corrida.error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>La redacción no terminó</AlertTitle>
        <AlertDescription>
          <p>{corrida.error.mensaje}</p>
        </AlertDescription>
      </Alert>
    );
  }

  if (corrida.estado !== "ok" || (conError === 0 && omitidas === 0)) return null;

  const partes = [plural(redactadas, "redactada", "redactadas")];
  if (conError > 0) partes.push(`${conError} con error`);
  if (omitidas > 0) partes.push(plural(omitidas, "omitida", "omitidas"));

  return (
    <Alert>
      <AlertTitle>Última redacción</AlertTitle>
      <AlertDescription>
        <p>{partes.join(" · ")}</p>
        <p>Las que fallaron o se omitieron siguen pendientes: ábrelas en el árbol para reintentarlas.</p>
      </AlertDescription>
    </Alert>
  );
}
